import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmDialog from '../../../../shared/components/confirm-dialog/ConfirmDialog';
import AddOnEditorPage from '../editor-page/AddOnEditorPage';
import useResolvedServiceRoute from '../../hooks/useResolvedServiceRoute';
import { getServicePathSegment } from '../../utils/serviceSlug';
import { upsertService } from '../../../../api/companyRecords';
import '../../styles/services.css';
import '../addons-v2.css';

export default function AddOnDetailsPage() {
  const { serviceId: routeParam, addOnId } = useParams();
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const { status, service: serviceDoc, firestoreId } = useResolvedServiceRoute(routeParam);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const currentAddOns = serviceDoc?.addOns || [];
  const isNew = addOnId === 'new';

  const addOn = useMemo(() => {
    if (isNew) return null;
    return currentAddOns.find((a) => a.id === addOnId) || null;
  }, [currentAddOns, isNew, addOnId]);

  const backToServiceDetailsAddons = () => {
    const path = getServicePathSegment(serviceDoc) || routeParam;
    navigate(`/app/overview/services/${path}?tab=addons`);
  };

  const handleSaveAddOn = async (addOnData) => {
    if (!companyData?.id || !firestoreId) return;
    try {
      const formatted = {
        ...addOnData,
        id: addOnData.id || `addon-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: addOnData.name,
        searchName: String(addOnData.name || '').toLowerCase(),
        price: Number(addOnData.price || 0),
        duration: Number(addOnData.duration || 0),
        imageUrl: addOnData.imageUrl || '',
        createdAt: addOnData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const existing = currentAddOns.some((a) => a.id === formatted.id);
      const updatedAddOns = existing ? currentAddOns.map((a) => (a.id === formatted.id ? formatted : a)) : [...currentAddOns, formatted];

      await upsertService({
        companyId: companyData.id,
        id: firestoreId,
        service: {
          addOns: updatedAddOns,
          addOnsCount: updatedAddOns.length,
          updatedAt: new Date().toISOString(),
        },
      });

      backToServiceDetailsAddons();
    } catch (error) {
      console.error('Error saving add-on:', error);
      alert(`Error saving add-on: ${error.message}. Please try again.`);
    }
  };

  const handleDelete = async () => {
    if (!companyData?.id || !firestoreId || !addOnId || isNew) return;
    try {
      const updated = currentAddOns.filter((a) => a.id !== addOnId);
      await upsertService({
        companyId: companyData.id,
        id: firestoreId,
        service: {
          addOns: updated,
          addOnsCount: updated.length,
          updatedAt: new Date().toISOString(),
        },
      });
      setConfirmDeleteOpen(false);
      backToServiceDetailsAddons();
    } catch (error) {
      console.error('Error deleting add-on:', error);
      alert('Error deleting add-on. Please try again.');
    }
  };

  if (status === 'loading' || status === 'idle') return null;
  if (status === 'not_found') return null;

  // Wait for service doc when editing an existing add-on.
  if (!isNew && !addOn) return null;

  return (
    <div className="svc-page">
      <div className="ao2-detailsTopbar">
        <button className="svc-btn svc-btn--ghost svc-btn--sm" type="button" onClick={backToServiceDetailsAddons}>
          <span className="ao2-backLong">← Back to Add-ons</span>
          <span className="ao2-backShort">← Back</span>
        </button>

        {!isNew ? (
          <button
            className="svc-btn"
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            style={{ borderColor: 'rgba(220,38,38,0.25)', color: 'var(--error-600)' }}
          >
            <span className="ao2-delLong">Delete Add-on</span>
            <span className="ao2-delShort">Delete</span>
          </button>
        ) : (
          <div />
        )}
      </div>

      <AddOnEditorPage open addOn={addOn} serviceId={firestoreId} busy={false} onClose={backToServiceDetailsAddons} onSave={handleSaveAddOn} />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete add-on?"
        message="This will permanently delete this add-on. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        busy={false}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

